import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import * as Icons from "lucide-react";
import { MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import CtaBanner from "@/components/CtaBanner";
import InlineCTA from "@/components/InlineCTA";
import { usePageContent, useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import useSeo from "@/lib/useSeo";

function getIcon(name) {
    return Icons[name] || Icons.HelpCircle;
}

export default function FAQ() {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t } = usePageContent("faq");
    const { tr } = useLocale();
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": sections.flatMap((s) =>
            (s.items || []).slice(0, 50).map((it) => ({
                "@type": "Question",
                "name": it.question,
                "acceptedAnswer": { "@type": "Answer", "text": it.answer },
            }))
        ),
    };

    useSeo({
        title: tr("FAQ — Perguntas frequentes NOVIFOOD", "FAQ — NOVIFOOD frequently asked questions"),
        description: tr(
            "Respostas às perguntas mais comuns sobre os modelos Alfa e Ômega, energia solar, branding, prazos e financiamento dos carrinhos NOVIFOOD.",
            "Answers to the most common questions about the Alfa and Omega models, solar power, branding, timelines and financing of NOVIFOOD carts."
        ),
        path: "/faq",
        jsonLd: faqJsonLd.mainEntity.length ? faqJsonLd : undefined,
        jsonLdId: "faq",
    });

    useEffect(() => {
        let alive = true;
        api.get("/faq")
            .then((r) => alive && setSections(r.data || []))
            .catch(() => alive && setSections([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    return (
        <>
            <section className="relative overflow-hidden border-b border-border gradient-navy" data-testid="faq-hero">
                <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
                <div className="container-x relative hero-y text-white">
                    <span className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-secondary">
                        <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                        {t("hero", "eyebrow", tr("Dúvidas frequentes", "Frequently asked questions"))}
                    </span>
                    <h1 className="mt-3 max-w-3xl display-xl">
                        {tr("Tudo o que precisa de ", "Everything you need to ")}<span className="serif-accent text-secondary">{tr("saber", "know")}</span>{tr(", num só lugar.", ", in one place.")}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/80 leading-relaxed">
                        {t("hero", "subtitle", tr(
                            "Respostas rápidas sobre modelos, logística e equipamentos adicionais.",
                            "Quick answers about models, logistics and additional equipment."
                        ))}{" "}
                        <a href="/contacto" className="text-secondary font-semibold link-underline">{tr("Fale connosco.", "Talk to us.")}</a>
                    </p>
                </div>
            </section>

            <section className="container-x section-y-sm" data-testid="faq-sections">
                {loading ? (
                    <div className="grid gap-6">
                        {[0, 1, 2].map((i) => <div key={i} className="h-40 rounded-xl border border-border bg-white animate-pulse" />)}
                    </div>
                ) : sections.length === 0 ? (
                    <p className="text-muted-foreground">{tr("Sem perguntas disponíveis de momento.", "No questions available at the moment.")}</p>
                ) : (
                    <div className="grid gap-14">
                        {sections.map((sec, secIdx) => {
                            const Icon = getIcon(sec.icon);
                            return (
                                <div key={sec.id} data-testid={`faq-section-${sec.id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <h2 className="font-heading text-3xl font-extrabold text-primary">{sec.title}</h2>
                                    </div>

                                    <Accordion type="single" collapsible className="mt-6 rounded-xl border border-border bg-white divide-y divide-border">
                                        {sec.items.map((it, i) => (
                                            <AccordionItem key={it.id} value={`${sec.id}-${i}`} className="border-b-0 px-5">
                                                <AccordionTrigger
                                                    data-testid={`faq-item-${sec.id}-${i}`}
                                                    className="py-5 text-left font-heading text-base md:text-lg font-semibold text-primary hover:no-underline"
                                                >
                                                    {it.question}
                                                </AccordionTrigger>
                                                <AccordionContent className="pb-5 text-muted-foreground leading-relaxed whitespace-pre-line">
                                                    {it.answer}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>

                                    {/* Inline WhatsApp CTA — after every 2nd section */}
                                    {secIdx > 0 && secIdx % 2 === 1 && (
                                        <div className="mt-8" data-testid={`faq-mid-cta-${secIdx}`}>
                                            <InlineCTA
                                                eyebrow={tr("Ficaram dúvidas?", "Still unsure?")}
                                                title={tr("Fala connosco no WhatsApp — resposta em minutos.", "Chat on WhatsApp — reply in minutes.")}
                                                to={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(tr("Olá NOVIFOOD, tenho uma dúvida", "Hi NOVIFOOD, I have a question"))}`}
                                                label={tr("Abrir WhatsApp", "Open WhatsApp")}
                                                external
                                                icon={MessageCircle}
                                                variant="ghost"
                                                testId={`faq-inline-cta-wpp-${secIdx}`}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <section className="container-x pb-20 lg:pb-28" data-testid="faq-final-cta">
                <CtaBanner
                    eyebrow={tr("Ainda tem dúvidas?", "Still have questions?")}
                    title={tr("Fale com a nossa equipa e tire todas as dúvidas.", "Talk to our team and get every question answered.")}
                    description={tr(
                        "Respondemos em menos de 6 horas e ajudamos a desenhar o carrinho ideal para o seu negócio — sem compromisso.",
                        "We reply in under 6 hours and help design the ideal cart for your business — no commitment."
                    )}
                    primary={{ to: "/contacto", label: tr("Pedir orçamento", "Request a quote") }}
                    secondary={{ to: `https://wa.me/${whatsappNumber}`, label: tr("Falar no WhatsApp", "Chat on WhatsApp"), external: true, icon: MessageCircle }}
                    variant="navy"
                    testId="faq-cta-banner"
                />
            </section>
        </>
    );
}
